// Full-screen mobile search experience. Tapping any search bar on mobile
// (navbar global search OR a per-tab DJPalSearchBar like Set Planner) opens
// this overlay; the inline dropdown is desktop-only.
//
// Why a separate page-style overlay instead of the inline dropdown:
//   - Scroll happens at the page level, so iOS Safari's native scroll-vs-tap
//     detection handles it correctly (no false-click on flick-scroll).
//   - Recent searches always show when the input is empty, regardless of
//     prior committed queries — the input is reset on open.
//   - Larger touch targets and roomier layout (SoundCloud-style).
//
// Two modes:
//   - default (no `external` prop): unified search, internally driven by
//     useUnifiedSearch (artists + tracks + mixes with section headers).
//   - `external={...}` mode: single-type search where the parent owns the
//     state and handlers — matches DJPalSearchBar's prop shape. Use this
//     to give per-tab search bars the same mobile UX.

import React, { useEffect, useRef } from 'react';
import useOverlayDismiss from './common/useOverlayDismiss';
import ReactDOM from 'react-dom';
import { useUnifiedSearch } from '../hooks/useUnifiedSearch';
import { MIN_SEARCH_CHARS } from '../utils/searchConfig';
import { eventKindLabel, eventSectionHeader } from '../utils/searchSections';
import { resolveMixCover, useNativeMixCoverMode } from '../utils/nativeMixCover';
import { entityId } from '../utils/entityId';
import './MobileSearchOverlay.scss';
import CoverArt from './CoverArt';

const Section = ({ label, children }) => (
    <div className="mso-section">
        <div className="mso-section-header">{label}</div>
        {children}
    </div>
);

const Artwork = ({ src, fallback, circle }) => {
    const radius = circle ? '50%' : 6;
    return src
        ? <CoverArt src={src} size={48} eager className="mso-artwork" style={{ borderRadius: radius }} />
        : <div className="mso-artwork mso-artwork-placeholder" style={{ borderRadius: radius }}>{fallback}</div>;
};

const RowChevron = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mso-row-chevron">
        <path d="M9 5l7 7-7 7" />
    </svg>
);

const MobileSearchOverlay = ({ open, onClose }) => {
    const search = useUnifiedSearch();
    useNativeMixCoverMode(); // re-render when the native cover flag flips
    const inputRef = useRef(null);
    const { closing, fading, requestClose } = useOverlayDismiss(open, onClose, inputRef);

    // Reset the input on each open so recent searches show first — even when
    // a prior committed query is still in Redux state. Lock body scroll so
    // the page behind doesn't scroll under the overlay.
    useEffect(() => {
        if (!open) return undefined;
        search.reset();
        const focusTimer = setTimeout(() => inputRef.current?.focus(), 50);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            clearTimeout(focusTimer);
            document.body.style.overflow = prevOverflow;
        };
    // search.reset is stable from useCallback; intentionally not depending on
    // it so re-creating the search object on each render doesn't re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const {
        query, setQuery, groups, sectionOrder, flat, loading,
        historyItems,
        navigate, reRunHistory, dismissHistoryEntry, clearAllHistory, submitFreeText, goToResultsPage,
    } = search;

    const showHistory = query.trim().length < MIN_SEARCH_CHARS && historyItems.length > 0;
    const handleSubmit = (e) => {
        e.preventDefault();
        submitFreeText();
        requestClose();
    };
    const handleSelect = (action) => () => { action(); requestClose(); };

    return ReactDOM.createPortal(
        <div className={`mobile-search-overlay${closing ? ' is-closing' : ''}${fading ? ' is-fading' : ''}`} role="dialog" aria-modal="true" aria-label="Search">
            <div className="mso-top">
                <form onSubmit={handleSubmit} className="mso-form">
                    <div className="mso-input-pill">
                        <svg className="mso-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input
                            ref={inputRef}
                            type="search"
                            inputMode="search"
                            enterKeyHint="search"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search"
                            className="mso-input"
                        />
                        {loading && <div className="mso-spinner" />}
                        {!loading && query && (
                            <button
                                type="button"
                                className="mso-input-clear"
                                onClick={() => setQuery('')}
                                aria-label="Clear search"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </div>
                </form>
                <button type="button" className="mso-cancel" onClick={requestClose}>Cancel</button>
            </div>

            <div className="mso-body">
                {showHistory && (
                    <Section label="Recently searched">
                        {historyItems.map((entry) => {
                            const isQuery = entry.entityType === 'query';
                            const primary = isQuery ? entry.queryText : entry.entityName;
                            const secondary = isQuery ? null : (
                                entry.entitySubtitle
                                || (entry.entityType === 'artist' ? 'Artist'
                                    : entry.entityType === 'track' ? 'Tracks' : 'Mixes')
                            );
                            const fallback = (primary || '?').charAt(0).toUpperCase();
                            return (
                                <button
                                    key={entry.id}
                                    type="button"
                                    className="mso-row mso-history-row"
                                    onClick={handleSelect(() => reRunHistory(entry))}
                                >
                                    {isQuery ? (
                                        <div className="mso-artwork mso-artwork-placeholder mso-history-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="9" />
                                                <polyline points="12 7 12 12 15 14" />
                                            </svg>
                                        </div>
                                    ) : (
                                        <Artwork src={entry.entityArtwork} fallback={fallback} circle={entry.entityType === 'artist'} />
                                    )}
                                    <div className="mso-row-text">
                                        <span className="mso-row-primary">{primary}</span>
                                        {secondary && <span className="mso-row-secondary">{secondary}</span>}
                                    </div>
                                    <button
                                        type="button"
                                        className="mso-row-dismiss"
                                        onClick={(e) => { e.stopPropagation(); dismissHistoryEntry(entry.id); }}
                                        aria-label="Remove from recent searches"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                </button>
                            );
                        })}
                        <button type="button" className="mso-clear-all" onClick={clearAllHistory}>
                            Clear recent searches
                        </button>
                    </Section>
                )}

                {!showHistory && flat.length > 0 && (
                    <>
                        {flat[0]?.type === 'all' && (
                            <button
                                type="button"
                                className="mso-row mso-all-row"
                                onClick={handleSelect(() => goToResultsPage(flat[0].query))}
                            >
                                <div className="mso-artwork mso-artwork-placeholder mso-all-icon">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="7" />
                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                </div>
                                <div className="mso-row-text">
                                    <span className="mso-row-primary">
                                        See all results for <strong>&ldquo;{flat[0].query}&rdquo;</strong>
                                    </span>
                                </div>
                                <RowChevron />
                            </button>
                        )}

                        {/* Sections render in the hook's sectionOrder (scored
                            per fetch) so all surfaces agree with keyboard nav. */}
                        {sectionOrder.map((section) => {
                            if (section === 'events') {
                                return (groups.events || []).length > 0 && (
                                    <Section key="events" label={eventSectionHeader(groups.events)}>
                                        {groups.events.map((ev) => (
                                            <button
                                                key={`ev-${ev.name}`}
                                                type="button"
                                                className="mso-row"
                                                onClick={handleSelect(() => navigate({ type: 'event', item: ev }))}
                                            >
                                                <Artwork src={ev.covers?.[0]} fallback={(ev.name || '?').charAt(0).toUpperCase()} />
                                                <div className="mso-row-text">
                                                    <span className="mso-row-primary">{ev.name}</span>
                                                    <span className="mso-row-secondary">{eventKindLabel(ev.kind)} · {ev.mixCount} mixes</span>
                                                </div>
                                                <RowChevron />
                                            </button>
                                        ))}
                                    </Section>
                                );
                            }
                            if (section === 'labels') {
                                return (groups.labels || []).length > 0 && (
                                    <Section key="labels" label="Labels">
                                        {groups.labels.map((l) => (
                                            <button
                                                key={l.publicId || l.id || l.name}
                                                type="button"
                                                className="mso-row"
                                                onClick={handleSelect(() => navigate({ type: 'label', item: l }))}
                                            >
                                                <Artwork src={l.artworkUrl} fallback={(l.name || '?').charAt(0).toUpperCase()} />
                                                <div className="mso-row-text">
                                                    <span className="mso-row-primary">{l.name}</span>
                                                    <span className="mso-row-secondary">{l.trackCount ? `Label · ${l.trackCount} tracks` : 'Label'}</span>
                                                </div>
                                                <RowChevron />
                                            </button>
                                        ))}
                                    </Section>
                                );
                            }
                            if (section === 'artists') {
                                return groups.artists.length > 0 && (
                                    <Section key="artists" label="Artists">
                                        {groups.artists.map((a) => {
                                            const genres = Array.isArray(a.genres) ? a.genres.join(', ') : a.genres;
                                            return (
                                                <button
                                                    key={entityId(a) || a.name}
                                                    type="button"
                                                    className="mso-row"
                                                    onClick={handleSelect(() => navigate({ type: 'artist', item: a }))}
                                                >
                                                    <Artwork src={a.artworkUrl} fallback={(a.name || '?').charAt(0).toUpperCase()} circle />
                                                    <div className="mso-row-text">
                                                        <span className="mso-row-primary">{a.name}</span>
                                                        {genres && <span className="mso-row-secondary">{genres}</span>}
                                                    </div>
                                                    <RowChevron />
                                                </button>
                                            );
                                        })}
                                    </Section>
                                );
                            }
                            if (section === 'tracks') {
                                return groups.tracks.length > 0 && (
                                    <Section key="tracks" label="Tracks">
                                        {groups.tracks.map((t) => (
                                            <button
                                                key={entityId(t) || `${t.artist}-${t.title}`}
                                                type="button"
                                                className="mso-row"
                                                onClick={handleSelect(() => navigate({ type: 'track', item: t }))}
                                            >
                                                <Artwork src={t.artworkUrl} fallback={(t.title || '?').charAt(0).toUpperCase()} />
                                                <div className="mso-row-text">
                                                    <span className="mso-row-primary">{t.title}</span>
                                                    {t.artist && <span className="mso-row-secondary">{t.artist}</span>}
                                                </div>
                                                <RowChevron />
                                            </button>
                                        ))}
                                    </Section>
                                );
                            }
                            return groups.mixes.length > 0 && (
                                <Section key="mixes" label="Mixes">
                                    {groups.mixes.map((m) => (
                                        <button
                                            key={entityId(m) || `${m.djName}-${m.title}`}
                                            type="button"
                                            className="mso-row"
                                            onClick={handleSelect(() => navigate({ type: 'mix', item: m }))}
                                        >
                                            <Artwork src={resolveMixCover(m)} fallback={(m.title || '?').charAt(0).toUpperCase()} />
                                            <div className="mso-row-text">
                                                <span className="mso-row-primary">{m.title}</span>
                                                {m.djName && <span className="mso-row-secondary">{m.djName}</span>}
                                            </div>
                                            <RowChevron />
                                        </button>
                                    ))}
                                </Section>
                            );
                        })}
                    </>
                )}

                {!showHistory && flat.length === 0 && query.trim().length >= MIN_SEARCH_CHARS && !loading && (
                    <div className="mso-empty">No results for &ldquo;{query}&rdquo;</div>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default MobileSearchOverlay;
